import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { FaExclamationCircle, FaExclamationTriangle } from "react-icons/fa";
import { LuArrowRight, LuX } from "react-icons/lu";
import AppointmentCancellationModal from "@/components/appointment-cancellation-modal";
import { Button, Card } from "@/components/ui";
import { queryClient } from "@/config/react-query";
import { useSelfScheduleContext } from "@/contexts/self-schedule-provider";
import { useUpdateClaimedPatient } from "@/hooks/patient-claim/use-patient-claim";
import { usePatientVerification } from "@/hooks/self-schedule/use-patient-verification";
import { useAutoChargeCancellationFees } from "@/hooks/use-auto-charge-cancellation-fees";
import { buildExistingAppointmentSelectionSnapshot } from "@/lib/existing-appointment-state";
import { formatDateWithDay, formatList, formatTime } from "@/lib/format-data";
import { isVirtualServiceVisit, mixesDelivery } from "@/lib/self-schedule-virtual-services";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import { EventStatus } from "@/types/enums";
import { PublicEventTiny } from "@/types/event";
import { selfScheduleMutationOptions, selfScheduleQueryKeys, selfScheduleQueryOptions } from "@/api/check-in/self-schedule/self-schedule-query";
import { tenantQueryOptions } from "@/query/tenant";
import { LocationType, generateInstanceId, useSelfScheduleStore } from "@/stores/self-schedule-store";
interface ExistingAppointmentCardProps {
  targetAppointmentId?: string;
  canCancelAppointments?: boolean;
  canRescheduleAppointments?: boolean;
}
export const ExistingAppointmentCard = t0 => {
  const $ = _c(105);
  const {
    targetAppointmentId,
    canCancelAppointments: t1,
    canRescheduleAppointments: t2
  } = t0;
  const canCancelAppointments = t1 === undefined ? true : t1;
  const canRescheduleAppointments = t2 === undefined ? true : t2;
  const {
    setCancelEventId,
    setDismissedExistingAppointment,
    selectedDateTime
  } = useSelfScheduleContext();
  const {
    blockingAppointment,
    patient,
    allFutureAppointments,
    isBlocking
  } = usePatientVerification();
  const setPatient = useUpdateClaimedPatient();
  const [cancellingAppointment, setCancellingAppointment] = useState(null);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      ...selfScheduleQueryOptions.frontPage(),
      select: _temp
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const {
    data: frontPageData
  } = useQuery(t3);
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      ...tenantQueryOptions.public(),
      select: _temp2
    };
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const {
    data: allowMultipleBookingsForDifferentAppointments
  } = useQuery(t4);
  const autoChargeCancellationFees = useAutoChargeCancellationFees();
  const canDismiss = !blockingAppointment && allowMultipleBookingsForDifferentAppointments;
  let t5;
  if ($[2] !== frontPageData) {
    t5 = appointment => {
      if (!frontPageData) {
        toast({
          title: "Error",
          description: "Missing appointment information.",
          variant: "error"
        });
        return;
      }
      const location = frontPageData.locations?.find(l => l.id === appointment.location?.id);
      const snapshot = buildExistingAppointmentSelectionSnapshot(appointment, frontPageData.services ?? [], frontPageData.providers ?? [], location?.id ?? null, generateInstanceId);
      useSelfScheduleStore.setState({
        dismissedExistingAppointment: true,
        selectedServiceInstances: snapshot.selectedServiceInstances,
        selectedAddons: snapshot.selectedAddons,
        selectedProviderId: snapshot.selectedProviderId,
        serviceInstanceProviderIds: snapshot.serviceInstanceProviderIds,
        serviceInstanceProviderOrigins: snapshot.serviceInstanceProviderOrigins,
        providerPreferenceMode: snapshot.providerPreferenceMode,
        addonProviderIds: snapshot.addonProviderIds,
        preferredProviderEntryKeys: snapshot.preferredProviderEntryKeys,
        selectedAvailabilityAssignments: snapshot.selectedAvailabilityAssignments,
        confirmedAppointmentReview: snapshot.confirmedAppointmentReview,
        ...(location ? {
          locationType: appointment.isVirtual ? LocationType.VIRTUAL : LocationType.IN_PERSON,
          selectedLocationId: location.id,
          selectedSlotLocationId: location.id
        } : {}),
        selectedDateTime: new Date(appointment.start),
        createdAppointmentId: appointment.id,
        confirmedServiceSelections: snapshot.selectedServiceInstances.length > 0,
        paymentSuccessful: false,
        paymentMethodCollected: false,
        paymentDeadline: null,
        confirmed: true,
        cancelEventId: null
      });
    };
    $[2] = frontPageData;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const handleCompletePayment = t5;
  let t6;
  if ($[4] !== frontPageData || $[5] !== isBlocking) {
    t6 = appointment_0 => {
      if (!frontPageData) {
        return;
      }
      if (isBlocking(appointment_0)) {
        return;
      }
      const location_0 = isVirtualServiceVisit(appointment_0) ? undefined : frontPageData.locations?.find(l_0 => l_0.id === appointment_0.location?.id);
      const snapshot_0 = buildExistingAppointmentSelectionSnapshot(appointment_0, frontPageData.services ?? [], frontPageData.providers ?? [], location_0?.id ?? null, generateInstanceId);
      useSelfScheduleStore.setState({
        dismissedExistingAppointment: true,
        selectedServiceInstances: snapshot_0.selectedServiceInstances,
        selectedAddons: snapshot_0.selectedAddons,
        selectedProviderId: snapshot_0.selectedProviderId,
        serviceInstanceProviderIds: snapshot_0.serviceInstanceProviderIds,
        serviceInstanceProviderOrigins: snapshot_0.serviceInstanceProviderOrigins,
        providerPreferenceMode: snapshot_0.providerPreferenceMode,
        addonProviderIds: snapshot_0.addonProviderIds,
        preferredProviderEntryKeys: snapshot_0.preferredProviderEntryKeys,
        selectedAvailabilityAssignments: [],
        confirmedAppointmentReview: snapshot_0.confirmedAppointmentReview,
        ...(location_0 ? {
          locationType: location_0.isVirtual ? LocationType.VIRTUAL : LocationType.IN_PERSON,
          selectedLocationId: location_0.id
        } : {
          locationType: appointment_0.isVirtual ? LocationType.VIRTUAL : null,
          selectedLocationId: null
        }),
        selectedDateTime: null,
        createdAppointmentId: null,
        confirmedServiceSelections: snapshot_0.selectedServiceInstances.length > 0,
        paymentSuccessful: false,
        paymentMethodCollected: false,
        paymentDeadline: null,
        confirmed: false,
        cancelEventId: appointment_0.id
      });
    };
    $[4] = frontPageData;
    $[5] = isBlocking;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const handleReschedule = t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = selfScheduleMutationOptions.cancelAppointment();
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== patient || $[9] !== setCancelEventId || $[10] !== setDismissedExistingAppointment || $[11] !== setPatient) {
    t8 = {
      ...t7,
      onSuccess: (_, t9) => {
        const {
          appointmentId
        } = t9;
        setCancelEventId(null);
        useSelfScheduleStore.setState({
          confirmed: false
        });
        queryClient.invalidateQueries({
          queryKey: selfScheduleQueryKeys.availability()
        });
        if (patient?.id) {
          const remaining = patient.futureAppointments.filter(a => a.id !== appointmentId);
          setPatient(p => p ? {
            ...p,
            futureAppointments: remaining
          } : p);
          if (remaining.length === 0) {
            setDismissedExistingAppointment(true);
          }
        }
        toast({
          title: "Appointment Cancelled",
          description: "Your appointment has been successfully cancelled.",
          variant: "success"
        });
      },
      onError: _temp3
    };
    $[8] = patient;
    $[9] = setCancelEventId;
    $[10] = setDismissedExistingAppointment;
    $[11] = setPatient;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const {
    mutateAsync: cancelAppointment,
    isPending: isCancelling
  } = useMutation(t8);
  let t9;
  if ($[13] !== cancelAppointment || $[14] !== cancellingAppointment) {
    t9 = async () => {
      if (!cancellingAppointment) {
        return;
      }
      await cancelAppointment({
        appointmentId: cancellingAppointment.id,
        cancellationReason: "Patient cancelled their appointment"
      });
      setCancellingAppointment(null);
    };
    $[13] = cancelAppointment;
    $[14] = cancellingAppointment;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const handleCancellationConfirm = t9;
  const hasAutoRescheduled = useRef(false);
  let t10;
  if ($[16] !== allFutureAppointments || $[17] !== canRescheduleAppointments || $[18] !== frontPageData || $[19] !== handleReschedule || $[20] !== isBlocking || $[21] !== targetAppointmentId) {
    t10 = () => {
      if (!targetAppointmentId || !frontPageData || hasAutoRescheduled.current) {
        return;
      }
      const target = allFutureAppointments.find(a_0 => a_0.id === targetAppointmentId);
      if (!canRescheduleAppointments || !target || isBlocking(target) || target.status === EventStatus.RESERVED || mixesDelivery(target)) {
        return;
      }
      hasAutoRescheduled.current = true;
      handleReschedule(target);
    };
    $[16] = allFutureAppointments;
    $[17] = canRescheduleAppointments;
    $[18] = frontPageData;
    $[19] = handleReschedule;
    $[20] = isBlocking;
    $[21] = targetAppointmentId;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== allFutureAppointments || $[24] !== canRescheduleAppointments || $[25] !== frontPageData || $[26] !== targetAppointmentId) {
    t11 = [targetAppointmentId, allFutureAppointments, frontPageData, canRescheduleAppointments];
    $[23] = allFutureAppointments;
    $[24] = canRescheduleAppointments;
    $[25] = frontPageData;
    $[26] = targetAppointmentId;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  useEffect(t10, t11);
  if (allFutureAppointments.length === 0) {
    return null;
  }
  let t12;
  if ($[28] !== allFutureAppointments || $[29] !== targetAppointmentId) {
    t12 = targetAppointmentId ? allFutureAppointments.filter(a_1 => a_1.id === targetAppointmentId) : allFutureAppointments;
    $[28] = allFutureAppointments;
    $[29] = targetAppointmentId;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const displayedAppointments = t12;
  if (displayedAppointments.length === 0) {
    return null;
  }
  const hasMultiple = displayedAppointments.length > 1;
  let t13;
  if ($[31] !== displayedAppointments || $[32] !== isBlocking) {
    let t14;
    if ($[34] !== isBlocking) {
      t14 = a_2 => isBlocking(a_2) && a_2.status !== EventStatus.RESERVED;
      $[34] = isBlocking;
      $[35] = t14;
    } else {
      t14 = $[35];
    }
    t13 = displayedAppointments.every(t14);
    $[31] = displayedAppointments;
    $[32] = isBlocking;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const allBlocking = t13;
  const t14 = allBlocking ? "border-2 border-status-error-text bg-status-error-bg shadow-lg" : "border border-border shadow-md";
  let t15;
  if ($[36] !== t14) {
    t15 = cx("p-6", "motion-ease-spring-bouncy", "motion-scale-in-105 motion-opacity-in-85", t14);
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  const t16 = allBlocking ? "text-status-error-text" : "text-foreground";
  let t17;
  if ($[38] !== t16) {
    t17 = cx("flex items-center gap-2 text-h3", t16);
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== allBlocking) {
    t18 = allBlocking ? <FaExclamationTriangle className="size-6 text-status-error-text" /> : <FaExclamationCircle className="size-5 text-decoda-blue" />;
    $[40] = allBlocking;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  const t19 = hasMultiple ? "You have upcoming appointments" : "You have an upcoming appointment";
  let t20;
  if ($[42] !== t19) {
    t20 = <span>{t19}</span>;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t17 || $[45] !== t18 || $[46] !== t20) {
    t21 = <h3 className={t17}>{t18}{t20}</h3>;
    $[44] = t17;
    $[45] = t18;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== canDismiss || $[49] !== selectedDateTime || $[50] !== setDismissedExistingAppointment) {
    t22 = canDismiss && !selectedDateTime && <Button variant="ghost" onClick={() => setDismissedExistingAppointment(true)} icon={<LuX />} className="inline-flex"><span>Dismiss</span></Button>;
    $[48] = canDismiss;
    $[49] = selectedDateTime;
    $[50] = setDismissedExistingAppointment;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== t21 || $[53] !== t22) {
    t23 = <div className="flex items-center justify-between">{t21}{t22}</div>;
    $[52] = t21;
    $[53] = t22;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== hasMultiple) {
    t24 = hasMultiple && <p className="text-body text-foreground-secondary">Select an appointment below to reschedule or cancel it.</p>;
    $[55] = hasMultiple;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  const t25 = hasMultiple ? "gap-3" : "gap-4";
  let t26;
  if ($[57] !== t25) {
    t26 = cx("flex flex-col", t25);
    $[57] = t25;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== canCancelAppointments || $[60] !== canDismiss || $[61] !== canRescheduleAppointments || $[62] !== cancellingAppointment?.id || $[63] !== displayedAppointments || $[64] !== handleCompletePayment || $[65] !== handleReschedule || $[66] !== isBlocking || $[67] !== isCancelling || $[68] !== selectedDateTime || $[69] !== setDismissedExistingAppointment) {
    let t28;
    if ($[71] !== canCancelAppointments || $[72] !== canDismiss || $[73] !== canRescheduleAppointments || $[74] !== cancellingAppointment?.id || $[75] !== displayedAppointments.length || $[76] !== handleCompletePayment || $[77] !== handleReschedule || $[78] !== isBlocking || $[79] !== isCancelling || $[80] !== selectedDateTime || $[81] !== setDismissedExistingAppointment) {
      t28 = appointment_1 => {
        const reserved = appointment_1.status === EventStatus.RESERVED;
        const within24h = isBlocking(appointment_1);
        const blocking = !reserved && within24h;
        const mixedVisit = mixesDelivery(appointment_1);
        const canReschedule = canRescheduleAppointments && !selectedDateTime && !reserved && !mixedVisit;
        const canCancel = canCancelAppointments && !blocking && !within24h;
        return <div key={appointment_1.id} className={cx(displayedAppointments.length > 1 && "rounded-lg border p-4 text-body", blocking ? "border-status-error-text bg-status-error-bg" : "border-border bg-background")}><p className={cx(blocking ? "font-medium text-status-error-text" : "text-foreground-secondary")}>{reserved ? <>You've reserved a time slot for a{" "}{formatList((appointment_1.segments || []).map(_temp4))}{" "}appointment on{" "}<strong>{formatDateWithDay(appointment_1.start)} at{" "}{formatTime(appointment_1.start)}</strong>{mixedVisit ? <>. Please call us to finish booking this visit.</> : <>. Please complete your payment or cancel your appointment to make a new one.</>}</> : <><strong>{formatList((appointment_1.segments || []).map(_temp5))}</strong>{" "}scheduled for{" "}<strong>{formatDateWithDay(appointment_1.start)} at{" "}{formatTime(appointment_1.start)}</strong>{blocking && <span>. You cannot cancel or reschedule appointments within 24 hours. Please call us to make changes.</span>}{!blocking && mixedVisit && <span>. Please call us to reschedule this visit.</span>}</>}</p>{!blocking && <div className="mt-3 flex justify-end gap-2">{canCancel && <Button variant="destructive" size={displayedAppointments.length > 1 ? "sm" : undefined} onClick={() => setCancellingAppointment(appointment_1)} isLoading={isCancelling && cancellingAppointment?.id === appointment_1.id} loadingText="Cancelling..."><span>Cancel</span></Button>}{canReschedule ? <Button size={displayedAppointments.length > 1 ? "sm" : undefined} onClick={() => handleReschedule(appointment_1)} disabled={isCancelling}><span>Reschedule</span></Button> : canDismiss && <Button variant="primary" size="sm" onClick={() => setDismissedExistingAppointment(true)} icon={<LuArrowRight />} className="inline-flex"><span>Continue</span></Button>}{reserved && !mixedVisit && <Button size="sm" onClick={() => handleCompletePayment(appointment_1)}><span>Complete Payment</span></Button>}</div>}</div>;
      };
      $[71] = canCancelAppointments;
      $[72] = canDismiss;
      $[73] = canRescheduleAppointments;
      $[74] = cancellingAppointment?.id;
      $[75] = displayedAppointments.length;
      $[76] = handleCompletePayment;
      $[77] = handleReschedule;
      $[78] = isBlocking;
      $[79] = isCancelling;
      $[80] = selectedDateTime;
      $[81] = setDismissedExistingAppointment;
      $[82] = t28;
    } else {
      t28 = $[82];
    }
    t27 = displayedAppointments.map(t28);
    $[59] = canCancelAppointments;
    $[60] = canDismiss;
    $[61] = canRescheduleAppointments;
    $[62] = cancellingAppointment?.id;
    $[63] = displayedAppointments;
    $[64] = handleCompletePayment;
    $[65] = handleReschedule;
    $[66] = isBlocking;
    $[67] = isCancelling;
    $[68] = selectedDateTime;
    $[69] = setDismissedExistingAppointment;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  let t28;
  if ($[83] !== t26 || $[84] !== t27) {
    t28 = <div className={t26}>{t27}</div>;
    $[83] = t26;
    $[84] = t27;
    $[85] = t28;
  } else {
    t28 = $[85];
  }
  let t29;
  if ($[86] !== t23 || $[87] !== t24 || $[88] !== t28) {
    t29 = <div className="flex flex-col gap-4">{t23}{t24}{t28}</div>;
    $[86] = t23;
    $[87] = t24;
    $[88] = t28;
    $[89] = t29;
  } else {
    t29 = $[89];
  }
  let t30;
  if ($[90] !== t15 || $[91] !== t29) {
    t30 = <Card className={t15}>{t29}</Card>;
    $[90] = t15;
    $[91] = t29;
    $[92] = t30;
  } else {
    t30 = $[92];
  }
  const t31 = cancellingAppointment !== null;
  let t32;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = open => {
      if (!open) {
        setCancellingAppointment(null);
      }
    };
    $[93] = t32;
  } else {
    t32 = $[93];
  }
  let t33;
  if ($[94] !== cancellingAppointment) {
    t33 = cancellingAppointment ? {
      title: cancellingAppointment.title,
      fees: cancellingAppointment.fees
    } : undefined;
    $[94] = cancellingAppointment;
    $[95] = t33;
  } else {
    t33 = $[95];
  }
  let t34;
  if ($[96] !== autoChargeCancellationFees || $[97] !== handleCancellationConfirm || $[98] !== isCancelling || $[99] !== t31 || $[100] !== t33) {
    t34 = <AppointmentCancellationModal isOpen={t31} setIsOpen={t32} onConfirm={handleCancellationConfirm} event={t33} isLoading={isCancelling} actionType="cancel" hideSmsToggle={true} hideReasonInput={true} autoChargeCardOnFile={autoChargeCancellationFees} defaultCancellationReason="Patient cancelled their appointment" />;
    $[96] = autoChargeCancellationFees;
    $[97] = handleCancellationConfirm;
    $[98] = isCancelling;
    $[99] = t31;
    $[100] = t33;
    $[101] = t34;
  } else {
    t34 = $[101];
  }
  let t35;
  if ($[102] !== t30 || $[103] !== t34) {
    t35 = <>{t30}{t34}</>;
    $[102] = t30;
    $[103] = t34;
    $[104] = t35;
  } else {
    t35 = $[104];
  }
  return t35;
};
function _temp(data) {
  return {
    services: data?.services,
    providers: data?.providers,
    locations: data?.locations
  };
}
function _temp2(data_0) {
  return data_0?.allowMultipleBookingsForDifferentAppointments;
}
function _temp3() {
  toast({
    title: "Error",
    description: "Failed to cancel appointment. Please try again.",
    variant: "error"
  });
}
function _temp4(es) {
  return es.service.name;
}
function _temp5(es_0) {
  return es_0.service.name;
}